import { getDb, historyKey, rowKey, type HistoryRow } from './schema.ts';

export async function getHistory(
  source: string,
  comicId: string,
  readUnitId?: string,
): Promise<HistoryRow | undefined> {
  const db = await getDb();
  return db.get('history', historyKey(source, comicId, readUnitId));
}

export async function getLatestComicHistory(source: string, comicId: string): Promise<HistoryRow | undefined> {
  const db = await getDb();
  const tx = db.transaction('history', 'readonly');
  const idx = tx.store.index('by-lastSeen');
  let cursor = await idx.openCursor(null, 'prev');
  while (cursor) {
    if (cursor.value.source === source && cursor.value.comicId === comicId) {
      const row = cursor.value;
      await tx.done;
      return row;
    }
    cursor = await cursor.continue();
  }
  await tx.done;
  return undefined;
}

export async function recordHistory(
  input: Omit<HistoryRow, 'key' | 'lastSeen'> & { lastSeen?: number },
): Promise<void> {
  const db = await getDb();
  const row: HistoryRow = {
    key: historyKey(input.source, input.comicId, input.readUnitId),
    source: input.source,
    comicId: input.comicId,
    ...(input.readUnitId !== undefined ? { readUnitId: input.readUnitId } : {}),
    ...(input.readUnitTitle !== undefined ? { readUnitTitle: input.readUnitTitle } : {}),
    ...(input.readGroupId !== undefined ? { readGroupId: input.readGroupId } : {}),
    ...(input.readGroupTitle !== undefined ? { readGroupTitle: input.readGroupTitle } : {}),
    lastPage: input.lastPage,
    totalPages: input.totalPages,
    titleSnapshot: input.titleSnapshot,
    coverUrl: input.coverUrl,
    lastSeen: input.lastSeen ?? Date.now(),
  };
  await db.put('history', row);
}

export async function listRecentHistory(limit = 50): Promise<HistoryRow[]> {
  const db = await getDb();
  const tx = db.transaction('history', 'readonly');
  const idx = tx.store.index('by-lastSeen');
  const out: HistoryRow[] = [];
  let cursor = await idx.openCursor(null, 'prev');
  while (cursor && out.length < limit) {
    out.push(cursor.value);
    cursor = await cursor.continue();
  }
  await tx.done;
  return out;
}

/**
 * Cursor-paginated walk over the history store, ordered by `lastSeen` desc.
 *
 * Returns at most `limit` rows starting at index `offset` (0-indexed). We
 * use the IDB cursor's `advance()` to skip the prefix instead of materializing
 * the full list — keeps memory flat for users with thousands of read rows.
 *
 * `hasMore` is whether at least one more row exists past the returned slice.
 */
export async function listHistoryRange(
  offset: number,
  limit: number,
): Promise<{ rows: HistoryRow[]; hasMore: boolean }> {
  if (offset < 0 || !Number.isFinite(offset)) {
    throw new RangeError(`listHistoryRange: invalid offset ${offset}`);
  }
  if (limit <= 0 || !Number.isFinite(limit)) {
    throw new RangeError(`listHistoryRange: invalid limit ${limit}`);
  }
  const db = await getDb();
  const tx = db.transaction('history', 'readonly');
  const idx = tx.store.index('by-lastSeen');
  const out: HistoryRow[] = [];
  let cursor = await idx.openCursor(null, 'prev');
  if (cursor && offset > 0) {
    // `advance(0)` is invalid per IDB spec — guard so the trivial
    // first-page case doesn't crash on Safari.
    cursor = await cursor.advance(offset);
  }
  while (cursor && out.length < limit) {
    out.push(cursor.value);
    cursor = await cursor.continue();
  }
  // After grabbing `limit` rows, peek one more to determine `hasMore`.
  // `cursor` here is either the next row past our slice (hasMore=true) or
  // null (the walk ran out).
  const hasMore = cursor !== null;
  await tx.done;
  return { rows: out, hasMore };
}

export async function deleteHistory(source: string, comicId: string): Promise<void> {
  const db = await getDb();
  await db.delete('history', rowKey(source, comicId));
}

export async function deleteReadUnitHistory(
  source: string,
  comicId: string,
  readUnitId?: string,
): Promise<void> {
  const db = await getDb();
  await db.delete('history', historyKey(source, comicId, readUnitId));
}

export async function clearHistory(): Promise<void> {
  const db = await getDb();
  await db.clear('history');
}
